import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { absenceChipStyle } from '../../../utils/absenceColors';
import { AlertTriangle, Car, Plus, Umbrella } from 'lucide-react';
import { type DayPlanning } from '../../../types/planning';
import { TaskBlock } from './TaskBlock';
import { DayCellMenu } from './DayCellMenu';
import { UtilizationBar } from '../../../components/Shared/UtilizationBar';
import { getConflictLabels } from '../../../utils/planningConstants';
import { DUE_TASK_DRAG_TYPE, type DueTaskDragData } from './DueTasksSidepanel';

function formatHours(hours: number): string {
  const v = Math.round((hours + Number.EPSILON) * 100) / 100;
  return `${v}h`;
}

function hoursTone(planned: number, target: number): string {
  if (target <= 0) return 'text-ink-muted dark:text-text-light';
  const ratio = planned / target;
  if (ratio >= 0.98) return 'text-status-full';
  if (ratio >= 0.85) return 'text-status-partial';
  return 'text-status-under';
}

interface DayCellProps {
  day: DayPlanning;
  employeeId: string;
  employeeName: string;
  /** Verwaltung/SuperAdmin — their absences are drawn solid instead of tinted (FACF-94). */
  isBackOffice?: boolean;
  onDrop?: (employeeId: string, date: string) => void;
  onDueTaskDrop?: (taskId: string, employeeId: string, date: string, durationMinutes: number) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onTaskDragStart?: (taskId: string, employeeId: string, date: string) => void;
  onTaskDragEnd?: () => void;
  onTaskClick?: (taskId: string) => void;
  onAddTask?: (employeeId: string, date: string) => void;
  onAddAbsence?: (employeeId: string, date: string) => void;
  onAddWorkDay?: (employeeId: string, date: string) => void;
  onEditDay?: (employeeId: string, date: string) => void;
  onRemoveDay?: (employeeId: string, date: string) => void;
}

export function DayCell({
  day,
  employeeId,
  isBackOffice = false,
  onDrop,
  onDueTaskDrop,
  onDragOver,
  onTaskDragStart,
  onTaskDragEnd,
  onTaskClick,
  onAddTask,
  onAddAbsence,
  onAddWorkDay,
  onEditDay,
  onRemoveDay,
}: DayCellProps) {
  const { t } = useTranslation('planning');
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const dueTaskRaw = e.dataTransfer.getData(DUE_TASK_DRAG_TYPE);
    if (dueTaskRaw) {
      try {
        const data: DueTaskDragData = JSON.parse(dueTaskRaw);
        onDueTaskDrop?.(data.taskId, employeeId, day.date, data.estimatedDurationMinutes);
      } catch {
        // Invalid drag payload — ignore
      }
      return;
    }

    onDrop?.(employeeId, day.date);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
    onDragOver?.(e);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const hasMenu = !!(onAddTask || onAddAbsence || onAddWorkDay || onEditDay || onRemoveDay);
  const conflictLabels = getConflictLabels(day.planEntryConflictFlags);
  const showConflictCorner = !!(day.absenceConflict || day.hasConflict);

  const containerClasses = `
    relative min-h-[120px] min-w-0 p-2 border-r border-b border-gray-200 dark:border-gray-700
    transition-colors overflow-hidden flex flex-col
    ${day.type === 'absence'
      ? 'bg-surface-muted dark:bg-gray-800/60 bg-stripe-soft'
      : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50'}
    ${isDragOver ? 'ring-2 ring-inset ring-blue-400 dark:ring-blue-500 bg-blue-50/50 dark:bg-blue-900/20' : ''}
  `;

  // Empty state — full-cell quick-add
  if (day.type === 'empty' && !day.absenceConflict) {
    return (
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={containerClasses}
      >
        {onAddWorkDay ? (
          <button
            type="button"
            onClick={() => onAddWorkDay(employeeId, day.date)}
            title={t('workDay.titleCreate')}
            className="
              group w-full flex-1 flex flex-col items-center justify-center gap-1.5
              text-gray-300 dark:text-gray-600 hover:text-primary dark:hover:text-primary
              hover:bg-primary/5 dark:hover:bg-primary/10 transition rounded-md
            "
          >
            <span
              className="
                w-8 h-8 rounded-full border border-dashed border-current
                grid place-items-center
              "
            >
              <Plus size={14} />
            </span>
            <span className="text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              {t('empty.addDay')}
            </span>
          </button>
        ) : (
          <div className="flex-1" />
        )}
        {hasMenu && (
          <div className="absolute top-1 right-1">
            <DayCellMenu
              onAddTask={() => onAddTask?.(employeeId, day.date)}
              onAddAbsence={() => onAddAbsence?.(employeeId, day.date)}
              onAddWorkDay={() => onAddWorkDay?.(employeeId, day.date)}
              onEditDay={() => onEditDay?.(employeeId, day.date)}
              onRemoveDay={() => onRemoveDay?.(employeeId, day.date)}
              isAbsence={false}
              isWorkDay={false}
            />
          </div>
        )}
      </div>
    );
  }

  // Absence state — striped slate background with label
  if (day.type === 'absence') {
    const absenceTitle = isBackOffice
      ? t('absenceBackOffice', { type: day.absenceType })
      : day.absenceType;
    return (
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={containerClasses}
      >
        <div className="flex items-start justify-between gap-1 min-w-0 flex-shrink-0">
          <div
            className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium min-w-0 truncate"
            style={absenceChipStyle(day.absenceTypeColor, isBackOffice)}
            title={absenceTitle}
          >
            <Umbrella className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{day.absenceType}</span>
          </div>
          {hasMenu && (
            <DayCellMenu
              onAddTask={() => onAddTask?.(employeeId, day.date)}
              onAddAbsence={() => onAddAbsence?.(employeeId, day.date)}
              onAddWorkDay={() => onAddWorkDay?.(employeeId, day.date)}
              onEditDay={() => onEditDay?.(employeeId, day.date)}
              onRemoveDay={() => onRemoveDay?.(employeeId, day.date)}
              isAbsence
              isWorkDay={false}
            />
          )}
        </div>
        <div className="flex-1 flex items-end">
          <span className="text-[10px] uppercase tracking-wide text-ink-subtle dark:text-text-light">
            {t('absence.fullDay')}
          </span>
        </div>
      </div>
    );
  }

  // Workday state — header strip + UtilizationBar + tasks
  const firstStart = day.tasks[0]?.startTime;
  const toneClass = hoursTone(day.plannedHours, day.targetHours);
  const showUtilBar = day.targetHours > 0;

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={containerClasses}
    >
      {showConflictCorner && (
        <div
          className="absolute top-0 right-0 pointer-events-none z-10"
          title={
            day.absenceConflict
              ? `${day.absenceConflict.absenceTypeName} – ${t('conflict.reschedule')}`
              : conflictLabels.join(', ')
          }
        >
          <div
            className="
              w-0 h-0 border-t-[18px] border-l-[18px]
              border-t-status-conflict border-l-transparent
            "
          />
          <AlertTriangle
            size={9}
            strokeWidth={3}
            className="absolute -top-[15px] right-[2px] text-white pointer-events-none"
          />
        </div>
      )}

      <div className="flex items-start justify-between gap-1 min-w-0 flex-shrink-0">
        <div className="flex items-baseline gap-2 min-w-0 flex-1">
          <span className={`text-xs font-semibold text-tabular ${toneClass}`}>
            {formatHours(day.plannedHours)}
            {day.targetHours > 0 && (
              <span className="text-ink-subtle dark:text-text-light font-normal">
                {' '}/ {formatHours(day.targetHours)}
              </span>
            )}
          </span>
          {firstStart && (
            <span className="text-[10px] text-ink-subtle dark:text-text-light text-tabular ml-auto">
              {firstStart}
            </span>
          )}
        </div>
        {hasMenu && (
          <DayCellMenu
            onAddTask={() => onAddTask?.(employeeId, day.date)}
            onAddAbsence={() => onAddAbsence?.(employeeId, day.date)}
            onAddWorkDay={() => onAddWorkDay?.(employeeId, day.date)}
            onEditDay={() => onEditDay?.(employeeId, day.date)}
            onRemoveDay={() => onRemoveDay?.(employeeId, day.date)}
            isAbsence={false}
            isWorkDay={day.targetHours > 0}
          />
        )}
      </div>

      {showUtilBar && (
        <div className="mt-1 mb-1.5">
          <UtilizationBar planned={day.plannedHours} target={day.targetHours} height={2} />
        </div>
      )}

      {day.plannedLicensePlate && (
        <div
          className="mb-1.5 inline-flex items-center gap-1 self-start rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 dark:text-slate-300 font-mono"
          title={t('plannedVehicle', { plate: day.plannedLicensePlate })}
        >
          <Car className="w-3 h-3" aria-hidden />
          {day.plannedLicensePlate}
        </div>
      )}

      {day.tasks.length > 0 ? (
        <div className="space-y-1 min-w-0 flex-1">
          {day.tasks.map((task) => (
            <TaskBlock
              key={task.id}
              task={task}
              onDragStart={(taskId) => onTaskDragStart?.(taskId, employeeId, day.date)}
              onDragEnd={onTaskDragEnd}
              onClick={() => onTaskClick?.(task.id)}
            />
          ))}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-[10px] text-ink-faint dark:text-gray-600">
            {t('empty.noTasks')}
          </span>
        </div>
      )}
    </div>
  );
}
